import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  InputAdornment,
  Alert,
  Chip,
  Avatar,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { 
  AttachMoney as MoneyIcon, 
  TrendingUp as TrendingUpIcon,
  Delete as DeleteIcon,
  Speed as QuickIcon
} from '@mui/icons-material';
import { Product } from '../../types/models';

interface QuickPriceUpdateProps {
  open: boolean;
  onClose: () => void;
  product: Product | null;
  onUpdate: (productId: string, newPrice: number, originalPrice: number, weightPrices?: Array<{weight: string, price: number, originalPrice: number, stockQuantity?: number}>) => Promise<void>;
}

const QuickPriceUpdate: React.FC<QuickPriceUpdateProps> = ({
  open,
  onClose,
  product,
  onUpdate
}) => {
  const [newPrice, setNewPrice] = useState<number>(0);
  const [originalPrice, setOriginalPrice] = useState<number>(0);
  const [weightPrices, setWeightPrices] = useState<Array<{weight: string, price: number, originalPrice: number, stockQuantity?: number}>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (product) {
      setNewPrice(product.pricePerKg || product.price);
      setOriginalPrice(product.originalPrice || product.pricePerKg || product.price);
      
      // Initialize weight prices from product
      if (product.weightPrices && product.weightPrices.length > 0) {
        setWeightPrices(product.weightPrices.map(wp => ({
          weight: wp.weight,
          price: wp.price,
          originalPrice: wp.originalPrice || wp.price,
          stockQuantity: wp.stockQuantity || 0
        })));
      }
      
      setError('');
    }
  }, [product]);

  const getWeightTemplates = () => {
    const unit = product?.unit || 'kg';
    const templates: Record<string, string[]> = {
      'kg': ['250g', '500g', '1kg', '2kg', '5kg', '10kg'],
      'litre': ['250ml', '500ml', '1l', '2l', '5l', '10l'],
      'g': ['50g', '100g', '250g', '500g', '1kg'],
      'ml': ['50ml', '100ml', '250ml', '500ml', '1l'],
      'piece': ['1piece', '5pieces', '10pieces', '25pieces', '50pieces', '100pieces']
    };
    return templates[unit] || templates['kg'];
  };

  const parseWeightToMultiplier = (weightStr: string, baseUnit: string): number => {
    const normalized = weightStr.toLowerCase().trim().replace(/\s+/g, '');
    const numMatch = normalized.match(/([\d.]+)/);
    const numericValue = numMatch ? parseFloat(numMatch[1]) : 1;
    const unitPart = normalized.replace(/^([\d.]+)\s*/, '').trim();
    
    const conversionTable: Record<string, Record<string, number>> = {
      'kg': { 'kg': 1, 'g': 0.001, 'gram': 0.001 },
      'litre': { 'l': 1, 'litre': 1, 'ml': 0.001, 'millilitre': 0.001 },
      'piece': { 'piece': 1, 'pieces': 1, 'pc': 1, 'pcs': 1 },
      'g': { 'g': 1, 'gram': 1, 'kg': 1000 },
      'ml': { 'ml': 1, 'millilitre': 1, 'l': 1000, 'litre': 1000 }
    };

    const conversions = conversionTable[baseUnit.toLowerCase()] || {};
    const conversionFactor = conversions[unitPart] || 1;
    return numericValue * conversionFactor;
  };

  const handleCalculatePrices = () => {
    if (!newPrice || newPrice <= 0) {
      setError('Please set base selling price first');
      return;
    }

    if (weightPrices.length === 0) {
      setError('No weight options available');
      return;
    }

    const mrpPerKg = originalPrice || newPrice;
    const baseUnit = product?.unit || 'kg';
    
    const updatedWeightPrices = weightPrices.map(wp => {
      const multiplier = parseWeightToMultiplier(wp.weight, baseUnit);
      return {
        ...wp,
        price: Math.round(newPrice * multiplier),
        originalPrice: Math.round(mrpPerKg * multiplier)
      };
    });

    setWeightPrices(updatedWeightPrices);
    setError('');
  };

  const handleUpdate = async () => {
    if (!product) return;

    if (newPrice <= 0) {
      setError('Price must be greater than 0');
      return;
    }

    if (originalPrice > 0 && originalPrice <= newPrice) {
      setError('Original price should be higher than current price for discount');
      return;
    }

    setLoading(true);
    setError('');

    try {
      setLoading(true);
      await onUpdate(product.id, newPrice, originalPrice, weightPrices);
      setLoading(false);
      onClose();
    } catch (err) {
      setLoading(false);
      setError('Failed to update price. Please try again.');
    }
  };

  const getDiscountPercentage = () => {
    if (!originalPrice || originalPrice <= newPrice) return 0;
    return Math.round(((originalPrice - newPrice) / originalPrice) * 100);
  };

  const formatCurrency = (amount: number) => `₹${amount.toLocaleString()}`;

  if (!product) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      fullScreen={window.innerWidth < 600}
      PaperProps={{
        sx: {
          m: { xs: 0, sm: 2 },
          maxHeight: { xs: '100vh', sm: 'calc(100vh - 64px)' }
        }
      }}
    >
      <DialogTitle sx={{ 
        fontWeight: 700,
        pb: 2,
        px: { xs: 2, sm: 3 }
      }}>
        {product?.name}
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}


        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
          Quantity-Based Pricing
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <TextField
            label={`MRP per ${product?.unit || 'Kg'}`}
            type="number"
            size="small"
            value={originalPrice}
            onChange={(e) => setOriginalPrice(Number(e.target.value))}
            sx={{ flex: '1 1 150px', minWidth: 150 }}
            InputProps={{
              startAdornment: <InputAdornment position="start">₹</InputAdornment>
            }}
            inputProps={{ inputMode: 'decimal' }}
          />
          <TextField
            label={`Selling Price per ${product?.unit || 'Kg'}`}
            type="number"
            size="small"
            value={newPrice}
            onChange={(e) => setNewPrice(Number(e.target.value))}
            sx={{ flex: '1 1 150px', minWidth: 150 }}
            InputProps={{
              startAdornment: <InputAdornment position="start">₹</InputAdornment>
            }}
            inputProps={{ inputMode: 'decimal' }}
          />
          <FormControl size="small" sx={{ flex: '1 1 150px', minWidth: 150 }}>
            <InputLabel>Unit</InputLabel>
            <Select value={product?.unit || 'kg'} label="Unit" disabled>
              <MenuItem value="kg">Kilogram (kg)</MenuItem>
              <MenuItem value="litre">Litre (L)</MenuItem>
              <MenuItem value="g">Gram (g)</MenuItem>
              <MenuItem value="ml">Millilitre (ml)</MenuItem>
              <MenuItem value="piece">Piece</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Weight Options & Stock */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            Weight Options & Stock
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<QuickIcon />}
            onClick={handleCalculatePrices}
            disabled={!newPrice || newPrice <= 0}
            sx={{ textTransform: 'none' }}
          >
            Calculate Prices
          </Button>
        </Box>

        {weightPrices.length > 0 && (
          <Box>
            {weightPrices.map((wp, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ flex: '0 0 100px', minWidth: 100 }}>
                  <InputLabel>Weight</InputLabel>
                  <Select
                    value={wp.weight}
                    label="Weight"
                    onChange={(e) => {
                      const newWeightPrices = [...weightPrices];
                      newWeightPrices[index].weight = e.target.value;
                      setWeightPrices(newWeightPrices);
                    }}
                  >
                    {getWeightTemplates().map(w => (
                      <MenuItem key={w} value={w}>{w}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="MRP"
                  type="number"
                  size="small"
                  value={wp.originalPrice}
                  onChange={(e) => {
                    const newWeightPrices = [...weightPrices];
                    newWeightPrices[index].originalPrice = Number(e.target.value);
                    setWeightPrices(newWeightPrices);
                  }}
                  sx={{ flex: '1 1 100px', minWidth: 100 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>
                  }}
                  inputProps={{ inputMode: 'decimal' }}
                />
                <TextField
                  label="Selling Price"
                  type="number"
                  size="small"
                  value={wp.price}
                  onChange={(e) => {
                    const newWeightPrices = [...weightPrices];
                    newWeightPrices[index].price = Number(e.target.value);
                    setWeightPrices(newWeightPrices);
                  }}
                  sx={{ flex: '1 1 100px', minWidth: 100 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>
                  }}
                  inputProps={{ inputMode: 'decimal' }}
                />
                <TextField
                  label="Stock Quantity"
                  type="number"
                  size="small"
                  value={wp.stockQuantity || 0}
                  onChange={(e) => {
                    const newWeightPrices = [...weightPrices];
                    newWeightPrices[index].stockQuantity = Number(e.target.value);
                    setWeightPrices(newWeightPrices);
                  }}
                  sx={{ flex: '1 1 100px', minWidth: 100 }}
                  inputProps={{ inputMode: 'numeric' }}
                />
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => {
                    const newWeightPrices = weightPrices.filter((_, i) => i !== index);
                    setWeightPrices(newWeightPrices);
                  }}
                  sx={{ mt: 0.5 }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: { xs: 2, sm: 3 }, pt: 1, gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          fullWidth={window.innerWidth < 600}
          sx={{ order: { xs: 2, sm: 1 } }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleUpdate}
          variant="contained"
          disabled={loading}
          fullWidth={window.innerWidth < 600}
          sx={{ fontWeight: 600, order: { xs: 1, sm: 2 } }}
        >
          {loading ? 'Updating...' : 'Update Price'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickPriceUpdate;
